import React, {
  useCallback, useMemo, useRef, useState,
} from "react"
import {
  Box, Text, VStack,
} from "@chakra-ui/react"
import { BadgeElement } from "sharedTypes/badge"
import EditorPane from "components/CanvasEditor/EditorPane"
import { useBadgeTemplate } from "../../hooks/useBadgeTemplate"
import { useBadgeElement } from "../../contexts/BadgeElementContext"
import BadgeLayerItem from "./BadgeLayerItem"

const BadgeLayersSection = () => {
  const { currentTemplate } = useBadgeTemplate()
  const { handleCommitReorder } = useBadgeElement()

  const layers = useMemo(
    () => [...currentTemplate.badgeData.elements].sort((a, b) => b.zIndex - a.zIndex),
    [currentTemplate.badgeData.elements],
  )

  // The order while a layer is dragged, committed on release
  const [dragged, setDragged] = useState<BadgeElement[] | null>(null)
  const draggedRef = useRef(dragged)
  draggedRef.current = dragged
  const layersRef = useRef(layers)
  layersRef.current = layers

  const moveElement = useCallback((dragIndex: number, hoverIndex: number) => {
    setDragged((previous) => {
      const next = [...(previous ?? layersRef.current)]
      const [moved] = next.splice(dragIndex, 1)
      next.splice(hoverIndex, 0, moved)

      return next
    })
  }, [])

  const commitReorder = useCallback(() => {
    if (draggedRef.current) handleCommitReorder(draggedRef.current)
    setDragged(null)
  }, [handleCommitReorder])

  const shown = dragged ?? layers

  if (shown.length === 0) {
    return (
      <EditorPane>
        <Box p={4} textAlign="center">
          <Text color="gray.500" fontSize="sm">
            No elements yet
          </Text>
          <Text color="gray.400" fontSize="xs" mt={1}>
            Add elements to see them here
          </Text>
        </Box>
      </EditorPane>
    )
  }

  return (
    <EditorPane>
      <VStack spacing={2} align="stretch">
        {shown.map((element, index) => (
          <BadgeLayerItem
            key={element.id}
            element={element}
            index={index}
            onMoveElement={moveElement}
            onCommitReorder={commitReorder}
          />
        ))}
      </VStack>
    </EditorPane>
  )
}

export default BadgeLayersSection
